import { Button, Typography } from 'antd';
import format from 'date-fns/format';
import { enUS, nb } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRegistrations } from '../../fetchHooks/useRegistrations.ts';
import { useUserProfile } from '../../fetchHooks/useUserProfile.ts';
import NavMonths from '../Navbar/NavMonths.jsx';
import Navbar from '../Navbar/Navbar.tsx';
import type { DayType } from './Day.tsx';
import stylesSimple from './DaySimple.module.css';
import { DaySimple } from './DaySimple.tsx';
import { RenderDays } from './Days.tsx';
import { HourRateInputDebounced } from './HourRateInputDebounced.tsx';
import { MonthExport } from './MonthExport.jsx';
import { formatCurrency } from './dayUtils.ts';
import styles from './month.module.css';

const { Title } = Typography;
interface DayStats {
  earnedTotalMonth: number;
  workingDaysTotalMonth: number;
  workingHoursTotalMonth: number;
  flexitimeHoursTotalMonth: number;
  overtimeHoursTotalMonth: number;
  flexiTime: null | number;
}

const Month = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [showMonth, setShowMonth] = useState(Number.parseInt(searchParams.get('month') ?? '1'));
  const [showYear, setShowYear] = useState(
    Number.parseInt(searchParams.get('year') ?? new Date().getFullYear().toString()),
  );
  const [hourRateInput, setHourRateInput] = useState<number | undefined>(undefined);
  const [showExport, setShowExport] = useState(false);

  const { data: registrations, isLoading, isSuccess } = useRegistrations(showYear, showMonth);
  const { data: userProfile } = useUserProfile();
  const navigate = useNavigate();
  const isFurutoppen = userProfile?.username.toLowerCase() === 'furutoppen';

  useEffect(() => {
    showYear && showMonth && navigate(`/registrations?year=${showYear}&month=${showMonth}`);
  }, [showMonth, showYear, navigate]);

  useEffect(() => {
    const rate = registrations?.data?.at(1)?.hourRate;
    if (rate !== undefined) setHourRateInput(rate);
  }, [registrations]);

  const { data: month, ...stats }: { data: DayType[] } & DayStats = registrations || {
    data: [],
    earnedTotalMonth: 0,
    workingDaysTotalMonth: 0,
    workingHoursTotalMonth: 0,
    flexitimeHoursTotalMonth: 0,
    overtimeHoursTotalMonth: 0,
    flexiTime: null,
  };

  if (!showYear || !showMonth || (isSuccess && !month?.length)) navigate(`/`);

  const uniqueWeekNumbers = [];
  for (const { weekNumber } of month) {
    if (uniqueWeekNumbers.indexOf(weekNumber) === -1) {
      uniqueWeekNumbers.push(weekNumber);
    }
  }

  const monthName = format(new Date(showYear, showMonth - 1), 'MMMM', {
    locale: i18n.language === 'nb' ? nb : enUS,
  });
  const currentMonthFormatted = monthName.charAt(0).toUpperCase() + monthName.slice(1);
  if (isFurutoppen) {
    return (
      <div>
        <Navbar />
        <NavMonths year={showYear.toString()} month={showMonth} setMonth={setShowMonth} setYear={setShowYear} />

        <div className={styles.monthContainer}>
          <div>
            <Title style={{ textAlign: 'center' }}>
              Kalender for {currentMonthFormatted} {showYear}
            </Title>
          </div>

          <div>
            {uniqueWeekNumbers?.map((weekNumber) => {
              return (
                <div key={weekNumber}>
                  <div className={`${styles.weekWrapper}`}>
                    <p className={stylesSimple.weekdayMobile}>
                      {t('salary.week')} {weekNumber}:
                    </p>
                    <div className={`${styles.dayWrapper}`}>
                      <p className={styles.weekday}>
                        {t('salary.week')} {weekNumber}:
                      </p>
                    </div>
                    {month
                      .filter((date) => date.weekNumber === weekNumber)
                      .map((date) => (
                        <DaySimple date={date} key={date.id} />
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <NavMonths year={showYear.toString()} month={showMonth} setMonth={setShowMonth} setYear={setShowYear} />

      <div className={styles.monthContainer}>
        <Title className={styles.title}>
          {t('salary.hourlistFor')} {currentMonthFormatted} {showYear}
        </Title>
        <RenderDays month={month} uniqueWeekNumbers={uniqueWeekNumbers} isLoading={isLoading} />
        {/* MONTH SUMMARY */}
        <div className={`${styles.monthSummaryWrapper} ${styles.weekWrapper}`}>
          <div>
            <h3 className={styles.col1}>
              {t('salary.totalWorkingDays')}: <span className={styles.number}> {stats?.workingDaysTotalMonth}</span>
            </h3>
            <h3 className={styles.col1}>
              {t('salary.averageHoursPerDay')}:{' '}
              <span className={styles.number}>
                {(stats?.workingHoursTotalMonth / stats?.workingDaysTotalMonth).toFixed(2)}
              </span>
            </h3>
          </div>
          <div>
            <h3 className={styles.col2}>
              {t('salary.totalHours')}: <span className={styles.number}>{stats?.workingHoursTotalMonth}</span>
            </h3>
            <h3 className={styles.col2}>
              {t('salary.overtime')}: <span className={styles.number}>{stats?.overtimeHoursTotalMonth}</span>
            </h3>
          </div>
          <div>
            <h3 className={styles.col3}>
              {t('salary.invoiceAmount')}:{' '}
              <span className={styles.number}>{formatCurrency(stats?.earnedTotalMonth)}</span>
            </h3>
            <h3 className={styles.col3}>
              {t('salary.inclMVA')}:{' '}
              <span className={styles.number}>{formatCurrency(stats?.earnedTotalMonth * 1.25)}</span>
            </h3>
          </div>
          <h3 className={styles.col4}>
            {t('salary.flexitimeThisMonth')}:{' '}
            <span className={styles.number}>
              {registrations?.flexitimeHoursTotalMonth} {t('salary.hrs')}
            </span>
          </h3>
        </div>
        <div>
          <span className={styles.titleAndHourRate}>
            <HourRateInputDebounced
              label={t('salary.hourRate')}
              disabled={isLoading}
              value={hourRateInput}
              registration={registrations?.data?.at(1)}
              style={{ width: 200 }}
            />
            <Button
              disabled={isLoading}
              type="primary"
              onClick={() => setShowExport(true)}
              style={{ marginLeft: '2rem' }}
            >
              {t('salary.export')}
            </Button>
          </span>
        </div>
      </div>

      {/* ONLY FOR EXPORT VIEW */}
      <MonthExport
        showModal={showExport}
        onCancel={() => setShowExport(false)}
        fileName={`invoice-${currentMonthFormatted}-${showYear}`}
      >
        <h1 className={styles.exportTitle}>
          {t('salary.invoiceFor')} {currentMonthFormatted} {showYear}, {userProfile?.firstname} {userProfile?.lastname}
        </h1>
        <RenderDays isLoading={isLoading} month={month} uniqueWeekNumbers={uniqueWeekNumbers} isExport />
        <div className={styles.monthSummaryWrapper}>
          <h3 className={styles.col1}>
            {t('salary.totalWorkingDays')}: {stats?.workingDaysTotalMonth}
          </h3>
          <h3 className={styles.col2}>
            {t('salary.totalHours')}: {stats?.workingHoursTotalMonth}
          </h3>
          <h3 className={styles.col3}>
            {t('salary.invoiceAmountExMVA')}: {formatCurrency(stats?.earnedTotalMonth)}
          </h3>
          <h3 className={styles.col4}>
            {t('salary.includingMVA')}: {formatCurrency(stats?.earnedTotalMonth * 1.25)}
          </h3>
        </div>
      </MonthExport>
    </div>
  );
};

export default Month;
